// ===== Venue Dashboard - Main Dashboard Page (Backend Integrated) =====
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useVenueDashboard } from '../contexts/VenueDashboardContext';
import VenueDashboardHeader from '../components/VenueDashboardHeader';
import { 
  CameraIcon, 
  QrCodeIcon, 
  WifiIcon, 
  GiftIcon,
  CalendarIcon,
  StarIcon,
  CogIcon,
  UsersIcon,
  MapPinIcon,
  PowerIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

const VenueDashboardPage = () => {
  const { 
    venue, 
    streetVendor, 
    currentRole, 
    canAccess, 
    loading,
    error,
    offers,
    events,
    redemptions,
    refreshData,
    toggleVendorOperational,
    vendorId
  } = useVenueDashboard();
  
  const [activeTab, setActiveTab] = useState('venue');
  const [isOperational, setIsOperational] = useState(streetVendor?.isOperational ?? true);
  const [togglingStatus, setTogglingStatus] = useState(false);

  // Handle operational status toggle
  const handleToggleOperational = async () => {
    setTogglingStatus(true);
    const newStatus = !isOperational;
    setIsOperational(newStatus);
    await toggleVendorOperational(vendorId, newStatus);
    setTogglingStatus(false);
  };

  // Consumer role check
  if (currentRole === 'CONSUMER') {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
        <VenueDashboardHeader title="Dashboard" />
        <div style={{ padding: '40px 20px', textAlign: 'center' }}>
          <h2 style={{ fontSize: '24px', fontWeight: '600', color: '#1f2937' }}>
            Consumer Access
          </h2>
          <p style={{ color: '#6b7280', marginTop: '12px' }}>
            This dashboard is for venue staff. As a consumer, you have limited access.
          </p>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
        <VenueDashboardHeader title="Dashboard" showBack={false} />
        <div style={{ padding: '40px 20px', textAlign: 'center' }}>
          <ArrowPathIcon style={{ 
            width: '48px', 
            height: '48px', 
            color: '#3b82f6', 
            margin: '0 auto',
            animation: 'spin 1s linear infinite'
          }} />
          <p style={{ color: '#6b7280', marginTop: '16px' }}>Loading dashboard data...</p>
        </div>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Calculate stats from real data
  const todayRedemptions = redemptions?.filter(r => {
    const today = new Date().toDateString();
    return new Date(r.createdAt).toDateString() === today;
  }).length || 0;

  const activeOffers = offers?.filter(o => o.status === 'active' || o.isActive).length || offers?.length || 0;
  const upcomingEvents = events?.filter(e => new Date(e.date || e.startDate) > new Date()).length || events?.length || 0;

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      <VenueDashboardHeader title="Dashboard" showBack={false} />

      <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
        
        {/* Error Banner */}
        {error && (
          <div style={{
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '12px',
            padding: '12px 16px',
            marginBottom: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ color: '#dc2626', fontSize: '14px' }}>⚠️ Some data may be unavailable</span>
            <button
              onClick={refreshData}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: '#dc2626',
                color: 'white',
                fontSize: '12px',
                cursor: 'pointer'
              }}
            >
              Retry
            </button>
          </div>
        )}

        {/* Fallback indicator */}
        {venue?._fallback && (
          <div style={{
            backgroundColor: '#fffbeb',
            border: '1px solid #fcd34d',
            borderRadius: '12px',
            padding: '12px 16px',
            marginBottom: '16px',
            fontSize: '13px',
            color: '#92400e'
          }}>
            ⚠️ Using cached venue data - backend connection pending
          </div>
        )}
        
        {/* Tab Switcher */}
        <div style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '20px',
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '4px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <button
            onClick={() => setActiveTab('venue')}
            style={{
              flex: 1,
              padding: '12px 16px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: activeTab === 'venue' ? '#3b82f6' : 'transparent',
              color: activeTab === 'venue' ? 'white' : '#6b7280',
              fontWeight: '600',
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            data-testid="tab-venue"
          >
            🏢 Venue
          </button>
          <button
            onClick={() => setActiveTab('vendor')}
            style={{
              flex: 1,
              padding: '12px 16px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: activeTab === 'vendor' ? '#3b82f6' : 'transparent',
              color: activeTab === 'vendor' ? 'white' : '#6b7280',
              fontWeight: '600',
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            data-testid="tab-vendor"
          >
            🚗 Street Vendor
          </button>
        </div>

        {/* ===== VENUE TAB ===== */}
        {activeTab === 'venue' && (
          <>
            {/* Venue Info Card */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '24px',
              marginBottom: '20px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#1f2937', margin: '0 0 8px 0' }}>
                {venue?.name || venue?.venueName || 'Loading...'}
              </h2>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 4px 0' }}>
                {venue?.category || venue?.venuecategorydisplayname || venue?.venueCategory || 'Venue'}
              </p>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 12px 0' }}>
                📍 {venue?.city || 'Location'}
              </p>
              <div style={{
                display: 'inline-block',
                padding: '6px 12px',
                backgroundColor: '#dbeafe',
                borderRadius: '20px',
                fontSize: '13px',
                fontWeight: '600',
                color: '#1d4ed8'
              }}>
                Role: {currentRole}
              </div>
            </div>

            {/* Activity Stats - from backend data */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '12px',
              marginBottom: '24px'
            }}>
              <StatCard title="Redemptions (24h)" value={todayRedemptions} />
              <StatCard title="Active Offers" value={activeOffers} />
              <StatCard title="Upcoming Events" value={upcomingEvents} />
            </div>

            {/* Primary Actions */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '12px',
              marginBottom: '24px'
            }}>
              <ActionCard
                to="/venue-dashboard/redemption"
                icon={<CameraIcon style={{ width: '28px', height: '28px' }} />}
                title="Redemption Center"
                description="Scan & Redeem"
                color="#3b82f6"
                testId="action-redemption"
              />
              
              {canAccess(['STAFF', 'MANAGER', 'OWNER', 'ADMIN']) && (
                <>
                  <ActionCard
                    to="/venue-dashboard/staff-qr"
                    icon={<QrCodeIcon style={{ width: '28px', height: '28px' }} />}
                    title="Staff QR"
                    description="Generate Code"
                    color="#8b5cf6"
                    testId="action-staff-qr"
                  />
                  <ActionCard
                    to="/venue-dashboard/wifi-lab"
                    icon={<WifiIcon style={{ width: '28px', height: '28px' }} />}
                    title="Wi-Fi Lab"
                    description="Run Speed Test"
                    color="#10b981"
                    testId="action-wifi"
                  />
                </>
              )}
            </div>

            {/* Quick Access Grid */}
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', marginBottom: '12px' }}>
              Quick Access
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '12px'
            }}>
              {canAccess(['MANAGER', 'OWNER', 'ADMIN']) && (
                <>
                  <QuickAccessBtn to="/venue-dashboard/offers" icon={<GiftIcon />} label={`Offers (${offers?.length || 0})`} testId="quick-offers" />
                  <QuickAccessBtn to="/venue-dashboard/events" icon={<CalendarIcon />} label={`Events (${events?.length || 0})`} testId="quick-events" />
                  <QuickAccessBtn to="/venue-dashboard/vitals" icon={<CogIcon />} label="Vitals" testId="quick-vitals" />
                  <QuickAccessBtn to="/venue-dashboard/staff" icon={<UsersIcon />} label="Staff" testId="quick-staff" />
                </>
              )}
              <QuickAccessBtn to="/venue-dashboard/reviews" icon={<StarIcon />} label="Reviews" testId="quick-reviews" />
            </div>
          </>
        )}

        {/* ===== VENDOR TAB ===== */}
        {activeTab === 'vendor' && (
          <>
            {/* Vendor Info Card */}
            <div style={{
              backgroundColor: isOperational ? '#ecfdf5' : '#fef2f2',
              border: `2px solid ${isOperational ? '#10b981' : '#ef4444'}`,
              borderRadius: '16px',
              padding: '24px',
              marginBottom: '20px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#1f2937', margin: '0 0 8px 0' }}>
                    🚗 {streetVendor?.vendorName || "Ali's Morning Coffee Cart"}
                  </h2>
                  <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 8px 0' }}>
                    {streetVendor?.cuisineTags?.split(';')[0] || streetVendor?.vendorType || 'Coffee'}
                  </p>
                  {streetVendor?.lastLocation && (
                    <p style={{ 
                      fontSize: '12px', 
                      color: '#059669', 
                      fontFamily: 'monospace',
                      margin: 0
                    }}>
                      📍 {streetVendor.lastLocation.lat?.toFixed(6)}, {streetVendor.lastLocation.lng?.toFixed(6)}
                    </p>
                  )}
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: isOperational ? '#10b981' : '#ef4444'
                  }} />
                  <span style={{ fontWeight: '600', color: isOperational ? '#059669' : '#dc2626' }}>
                    {isOperational ? 'Live' : 'Offline'}
                  </span>
                </div>
              </div>

              {/* Operational Toggle */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginTop: '20px',
                padding: '12px 16px',
                backgroundColor: 'white',
                borderRadius: '12px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <PowerIcon style={{ width: '20px', height: '20px', color: '#6b7280' }} />
                  <span style={{ fontWeight: '500', color: '#374151' }}>Operational Status</span>
                </div>
                <button
                  onClick={handleToggleOperational}
                  disabled={togglingStatus}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '20px',
                    border: 'none',
                    backgroundColor: isOperational ? '#10b981' : '#ef4444',
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '13px',
                    cursor: togglingStatus ? 'wait' : 'pointer',
                    opacity: togglingStatus ? 0.7 : 1
                  }}
                  data-testid="operational-toggle"
                >
                  {togglingStatus ? '...' : isOperational ? '🟢 Open' : '🔴 Closed'}
                </button>
              </div>

              {streetVendor?._fallback && (
                <div style={{
                  marginTop: '12px',
                  padding: '8px 12px',
                  backgroundColor: '#fffbeb',
                  borderRadius: '8px',
                  fontSize: '12px',
                  color: '#92400e'
                }}>
                  ⚠️ Using cached vendor data
                </div>
              )}
            </div>

            {/* Vendor Stats */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '12px',
              marginBottom: '24px'
            }}>
              <StatCard title="Vendor ID" value={streetVendor?._id?.slice(-8) || '4889157f'} small />
              <StatCard title="Status" value={isOperational ? '🟢 Open' : '🔴 Closed'} />
              <StatCard title="Tracking" value={isOperational ? '✅ Active' : '⏸️ Idle'} />
            </div>

            {/* Vendor Details */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '12px',
              marginBottom: '24px'
            }}>
              <StatCard title="Cuisine" value={streetVendor?.cuisineTags?.split(';')[0] || 'Coffee'} />
              <StatCard title="Payment" value={streetVendor?.paymentTypes?.split(';')[0] || 'Cash'} />
              <StatCard title="Price" value={streetVendor?.priceRange || '$'} />
            </div>

            {/* Quick Access */}
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', marginBottom: '12px' }}>
              Quick Access
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '12px'
            }}>
              <QuickAccessBtn to="/" icon={<MapPinIcon />} label="Map" testId="quick-map" />
              <QuickAccessBtn to="/venue-dashboard/settings" icon={<CogIcon />} label="Settings" testId="quick-settings" />
              <QuickAccessBtn to="/venue-dashboard/reviews" icon={<StarIcon />} label="Reviews" testId="quick-reviews-vendor" />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Helper Components
const StatCard = ({ title, value, small = false }) => (
  <div style={{
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    borderLeft: '3px solid #3b82f6'
  }}>
    <h4 style={{ 
      fontSize: '12px', 
      fontWeight: '500', 
      color: '#6b7280', 
      margin: '0 0 4px 0',
      textTransform: 'uppercase'
    }}>
      {title}
    </h4>
    <p style={{ 
      fontSize: small ? '14px' : '20px', 
      fontWeight: '700', 
      color: '#1f2937', 
      margin: 0,
      fontFamily: small ? 'monospace' : 'inherit'
    }}>
      {value}
    </p>
  </div>
);

const ActionCard = ({ to, icon, title, description, color, testId }) => (
  <Link
    to={to}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      padding: '20px',
      backgroundColor: 'white',
      borderRadius: '16px',
      textDecoration: 'none',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      transition: 'all 0.2s ease',
      border: '1px solid #e5e7eb'
    }}
    data-testid={testId}
  >
    <div style={{
      width: '56px',
      height: '56px',
      borderRadius: '12px',
      backgroundColor: `${color}15`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: color
    }}>
      {icon}
    </div>
    <div>
      <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
        {title}
      </h3>
      <p style={{ fontSize: '13px', color: '#6b7280', margin: '4px 0 0 0' }}>
        {description}
      </p>
    </div>
  </Link>
);

const QuickAccessBtn = ({ to, icon, label, testId }) => (
  <Link
    to={to}
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '8px',
      padding: '16px 12px',
      backgroundColor: 'white',
      borderRadius: '12px',
      textDecoration: 'none',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      transition: 'all 0.2s ease'
    }}
    data-testid={testId}
  >
    <div style={{ color: '#6b7280', width: '24px', height: '24px' }}>
      {icon}
    </div>
    <span style={{ fontSize: '12px', fontWeight: '500', color: '#374151' }}>
      {label}
    </span>
  </Link>
);

export default VenueDashboardPage;
